import { Component, ReactNode } from "react";

const RELOAD_KEY = "__vite_stale_chunk_reload_at__";
const RELOAD_COOLDOWN_MS = 10_000;

const isStaleModuleError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Unable to preload CSS") ||
    /Loading (chunk|CSS chunk) [^ ]+ failed/i.test(message)
  );
};

const canAutoReload = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage can fail in restricted contexts; still allow the safest recovery.
  }
  return true;
};

type Props = { children: ReactNode };
type State = { error: Error | null; willReload: boolean };

class StaleModuleErrorBoundary extends Component<Props, State> {
  state: State = { error: null, willReload: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, willReload: isStaleModuleError(error) && canAutoReload() };
  }

  componentDidCatch(error: Error) {
    if (isStaleModuleError(error) && this.state.willReload) {
      window.setTimeout(() => window.location.reload(), 350);
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const isStale = isStaleModuleError(this.state.error);

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
        <div className="w-full max-w-xl space-y-4 rounded-lg border border-border bg-card p-5 shadow-lg">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Developer recovery overlay
            </p>
            <h1 className="font-serif text-2xl text-foreground">
              {isStale ? "Stale module import detected" : "App render failed"}
            </h1>
          </div>

          <p className="text-sm text-muted-foreground">
            {isStale
              ? this.state.willReload
                ? "A cached module could not be imported. Reloading now to fetch fresh assets."
                : "A cached module could not be imported. Automatic reload was skipped to prevent a loop."
              : "An unexpected render error occurred before this screen could finish loading."}
          </p>

          <pre className="max-h-40 overflow-auto rounded-md border border-border bg-muted p-3 text-xs text-muted-foreground whitespace-pre-wrap break-words">
            {this.state.error.message || String(this.state.error)}
          </pre>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
              onClick={() => this.setState({ error: null, willReload: false })}
            >
              Dismiss
            </button>
            <button
              type="button"
              className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              onClick={() => window.location.reload()}
            >
              Reload now
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default StaleModuleErrorBoundary;